import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, AlertTriangle, CalendarDays, Hash, FileText, Landmark, Building2, CreditCard, Banknote, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ExpenseForm } from '../components/ExpenseForm';
import { VoucherEntriesModal } from '../components/VoucherEntriesModal';
import { ExpenseRecordCreate } from '@/app/types/expense';
import { recordExpense } from '@/app/services/expenseService';
import { listSites } from '@/app/services/siteService';
import { Site } from '@/app/types/site';
import { getVoucher } from '@/app/services/voucherService';
import { VoucherResponse } from '@/app/types/voucher';
import { format, parseISO } from 'date-fns';
import { getAccount } from '@/app/services/accountService';
import { AccountResponse } from '@/app/types/account';
import { RecordLinkIcon } from '@/app/components/RecordLinkIcon';

// Helper to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(Number(amount));
};

// Helper to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return 'N/A';
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return format(date, 'dd MMM yyyy');
  } catch (error) {
    return String(dateString);
  }
};

const ExpenseDetailPage: React.FC = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const expenseId = id ? Number(id) : null;
  const isViewMode = !!expenseId;
  const isCreateMode = !expenseId;
  const [isEntriesModalOpen, setIsEntriesModalOpen] = useState(false);
  
  // Fetch Expense Voucher details (for view mode)
  const { data: expenseData, isLoading: isLoadingExpense, isError, error } = useQuery<VoucherResponse, Error>({
    queryKey: ['voucher', expenseId],
    queryFn: () => getVoucher(expenseId!),
    enabled: !!expenseId && !isNaN(expenseId),
  });


  // Fetch Account details for paid from/to accounts
  const paidFromAccountId = (expenseData as any)?.paid_from_account_id;
  const { data: paidFromAccount } = useQuery<AccountResponse, Error>({
    queryKey: ['account', paidFromAccountId],
    queryFn: () => getAccount(paidFromAccountId),
    enabled: !!paidFromAccountId,
  });

  const paidToAccountId = (expenseData as any)?.paid_to_account_id;
  const { data: paidToAccount } = useQuery<AccountResponse, Error>({
    queryKey: ['account', paidToAccountId],
    queryFn: () => getAccount(paidToAccountId),
    enabled: !!paidToAccountId,
  });

  // Fetch Sites (for create mode)
  const { data: sitesData, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 }),
    enabled: isCreateMode,
  });
  
  const sites: Site[] = sitesData?.items || [];

  // --- Mutations ---
  const createExpenseMutation = useMutation({
    mutationFn: (data: ExpenseRecordCreate) => recordExpense(data),
    onSuccess: (createdVoucher) => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      if (createdVoucher?.voucher_type_id) {
        queryClient.invalidateQueries({ queryKey: ['vouchers', { voucherTypeId: createdVoucher.voucher_type_id }] });
      }
      toast.success('Expense recorded successfully!');
      navigate('/expenses/list');
    },
    onError: (error: any) => {
      const errorMsg = error?.response?.data?.detail || error?.message || "Unknown error";
      console.error("Expense creation failed:", error);
      toast.error(`Failed to record expense: ${errorMsg}`);
    },
  });


  // --- Form Submit Handler ---
  const handleSubmit = (data: ExpenseRecordCreate): void => {
    console.log('Submitting expense data:', data);
    createExpenseMutation.mutate(data);
  };

  // --- Loading and Error States ---
  const isLoading = isViewMode ? isLoadingExpense : isLoadingSites;
  const isSubmitting = createExpenseMutation.isPending;

  // Determine expense type from voucher data
  const getExpenseType = () => {
    if (!expenseData) return null;
    const voucherTypeName = expenseData.voucher_type?.name?.toLowerCase();
    if (voucherTypeName?.includes('direct')) return 'Direct';
    if (voucherTypeName?.includes('adjustment')) return 'Adjustment';
    if (voucherTypeName?.includes('bill')) return 'Bill';
    return null;
  };

  // Display loading state
  if (isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-48" />
        </div>
        <Card>
          <CardHeader><Skeleton className="h-6 w-32" /></CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Display error state (for view mode)
  if (isViewMode && (isError || !expenseData)) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold tracking-tight text-destructive">Error</h1>
        </div>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading expense details: {error instanceof Error ? error.message : 'Expense not found or could not be loaded.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // View mode - Show expense details
  if (isViewMode && expenseData) {
    const expenseType = getExpenseType();
    
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-semibold tracking-tight">
              Expense Details: {expenseData.voucher_number}
            </h1>
            {expenseData.is_cancelled && <Badge variant="destructive">Cancelled</Badge>}
          </div>
          <Button
            variant="outline"
            onClick={() => setIsEntriesModalOpen(true)}
            className="gap-2"
          >
            <BookOpen className="h-4 w-4" />
            View Entries
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 text-sm">
            <DetailItem icon={CalendarDays} label="Date" value={formatDate(expenseData.transaction_date)} />
            <DetailItem icon={Hash} label="Voucher #" value={expenseData.voucher_number} />
            <DetailItem icon={FileText} label="Type" value={expenseData.voucher_type?.name || expenseType || 'N/A'} />
            
            <DetailItem icon={Building2} label="Site" value={expenseData.site?.name || 'N/A'} />
            <DetailItem icon={FileText} label="Reference #" value={expenseData.reference_number || '-'} />
            <DetailItem icon={Banknote} label="Total Amount" value={formatCurrency(expenseData.total_amount)} className="font-semibold text-base" />

            {/* Account details based on expense type */}
            {expenseType === 'Direct' && paidFromAccount && (
              <>
                <DetailItem icon={CreditCard} label="Paid From Account" value={paidFromAccount.name} />
              </>
            )}
            
            {expenseType === 'Adjustment' && (
              <>
                {paidFromAccount && (
                  <DetailItem icon={CreditCard} label="From Account" value={paidFromAccount.name} />
                )}
                {paidToAccount && (
                  <DetailItem icon={Landmark} label="To Account" value={paidToAccount.name} />
                )}
              </>
            )}

            {expenseData.narration && (
              <div className="md:col-span-3 mt-2">
                <p className="text-muted-foreground font-medium">Notes:</p>
                <p className="whitespace-pre-wrap">{expenseData.narration}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Voucher Entries Modal */}
        <VoucherEntriesModal
          isOpen={isEntriesModalOpen}
          onClose={() => setIsEntriesModalOpen(false)}
          voucherId={expenseId!}
          voucherNumber={expenseData.voucher_number}
        />
      </div>
    );
  }

  // Create mode - Show form
  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/expenses/list')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold tracking-tight">Record New Expense</h1>
      </div>

      {createExpenseMutation.isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error recording expense: {createExpenseMutation.error instanceof Error ? createExpenseMutation.error.message : 'An unknown error occurred'}
          </AlertDescription>
        </Alert>
      )}

      <ExpenseForm
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        sites={sites}
      />
    </div>
  );
};

// Helper component for consistent detail display
interface DetailItemProps {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  className?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ icon: Icon, label, value, className }) => (
  <div className={`flex items-start space-x-2 ${className}`}>
    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
    <div className="flex-1">
      <p className="text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  </div>
);

export default ExpenseDetailPage; 